import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const EMPTY_TREATMENT = { name: "", price: 0, duration_minutes: 45, machine_type_ids: [], machine_ids: [], active: true };
const EMPTY_USER = { name: "", email: "", password: "", role: "segreteria", physio_id: "", active: true };

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editTreatment, setEditTreatment] = useState(null);
  const [tForm, setTForm] = useState(EMPTY_TREATMENT);
  const [editUser, setEditUser] = useState(null);
  const [uForm, setUForm] = useState(EMPTY_USER);
  const [secForm, setSecForm] = useState({ turnstile_enabled: false, turnstile_site_key: "", turnstile_secret_key: "", max_attempts: 5, lockout_minutes: 15, session_hours: 12, twofa_enabled: false });
  const [tsTesting, setTsTesting] = useState(false);
  const [stForm, setStForm] = useState({ provider: "auto", local_dir: "", nas_dir: "", gdrive_folder_id: "", backup_provider: "nessuno", backup_dir: "", trash_enabled: true, max_upload_mb: 15 });
  const [stStatus, setStStatus] = useState(null);
  const [stTesting, setStTesting] = useState(false);
  const [commForm, setCommForm] = useState({ recipient_user_ids: [], recipient_roles: [] });
  const [permMeta, setPermMeta] = useState(null);
  const [permDraft, setPermDraft] = useState(null); // null = eredita dal ruolo

  const load = useCallback(async () => {
    const [s, t, mt, ph] = await Promise.all([
      api.get("/settings"), api.get("/treatments"), api.get("/machine-types"), api.get("/physios"),
    ]);
    setSettings(s.data); setTreatments(t.data); setMachineTypes(mt.data); setPhysios(ph.data);
    setSecForm({ turnstile_enabled: false, turnstile_site_key: "", turnstile_secret_key: "", max_attempts: 5, lockout_minutes: 15, session_hours: 12, twofa_enabled: false, ...(s.data.security || {}) });
    setStForm({ provider: "auto", local_dir: "", nas_dir: "", gdrive_folder_id: "", backup_provider: "nessuno", backup_dir: "", trash_enabled: true, max_upload_mb: 15, ...(s.data.storage || {}) });
    setStStatus(s.data._status || null);
    setCommForm({ recipient_user_ids: [], recipient_roles: [], ...(s.data.communications || {}) });
    api.get("/auth/permissions/meta").then(({ data }) => setPermMeta(data)).catch(() => {});
    if (user?.role === "admin") {
      const [u, l] = await Promise.all([api.get("/auth/users"), api.get("/logs", { params: { limit: 60 } })]);
      setUsers(u.data); setLogs(l.data);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTreatment = async () => {
    if (!tForm.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      if (editTreatment?.id) await api.put(`/treatments/${editTreatment.id}`, tForm);
      else await api.post("/treatments", tForm);
      toast.success("Trattamento salvato"); setEditTreatment(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delTreatment = async (t) => {
    if (!window.confirm(`Eliminare il trattamento "${t.name}"?`)) return;
    await api.delete(`/treatments/${t.id}`); load();
  };

  const saveSecurity = async () => {
    try {
      await api.put("/settings/security", {
        turnstile_enabled: secForm.turnstile_enabled,
        turnstile_site_key: secForm.turnstile_site_key,
        turnstile_secret_key: secForm.turnstile_secret_key,
        max_attempts: secForm.max_attempts,
        lockout_minutes: secForm.lockout_minutes,
        session_hours: secForm.session_hours,
        twofa_enabled: secForm.twofa_enabled,
      });
      toast.success("Impostazioni di sicurezza salvate");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const testTurnstile = async () => {
    setTsTesting(true);
    try {
      const { data } = await api.post("/settings/turnstile/test");
      toast.success(data.message || "Configurazione Turnstile valida");
    } catch (e) { toast.error(apiError(e, "Test configurazione fallito")); } finally { setTsTesting(false); }
  };

  const saveStorage = async () => {
    try {
      const { data } = await api.put("/settings/storage", stForm);
      toast.success(`Archiviazione salvata — provider attivo: ${data.provider_active}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const testStorage = async () => {
    setStTesting(true);
    try {
      const { data } = await api.post("/settings/storage/test");
      toast.success(`Test riuscito — provider "${data.provider}" funzionante`);
    } catch (e) { toast.error(apiError(e, "Test storage fallito")); } finally { setStTesting(false); }
  };

  const saveCommunications = async () => {
    try {
      await api.put("/settings/communications", commForm);
      toast.success("Destinatari segnalazioni salvati");
    } catch (e) { toast.error(apiError(e)); }
  };

  const savePerms = async () => {
    try {
      await api.put(`/auth/users/${editUser.id}/permissions`, { permissions: permDraft });
      toast.success(permDraft === null ? "Permessi ripristinati al preset del ruolo" : "Permessi personalizzati salvati");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePerm = (section, action) => {
    const base = permDraft ?? JSON.parse(JSON.stringify(permMeta?.presets?.[editUser?.role] || {}));
    const acts = new Set(base[section] || []);
    if (acts.has(action)) acts.delete(action); else acts.add(action);
    setPermDraft({ ...base, [section]: [...acts] });
  };

  const permAll = () => {
    const full = {};
    permMeta.sections.forEach((s) => { full[s.key] = permMeta.actions.map((a) => a.key); });
    setPermDraft(full);
  };

  const copyPermsFrom = (uid) => {
    const src = users.find((u) => u.id === uid);
    if (!src) return;
    const srcPerms = src.permissions ?? permMeta?.presets?.[src.role] ?? {};
    setPermDraft(JSON.parse(JSON.stringify(srcPerms)));
    toast.success(`Permessi copiati da ${src.name}`);
  };

  const saveUser = async () => {
    if (!uForm.name.trim() || !uForm.email.trim()) { toast.error("Nome ed email obbligatori"); return; }
    if (!editUser?.id && !uForm.password) { toast.error("Password obbligatoria"); return; }
    try {
      const body = { ...uForm, physio_id: uForm.physio_id || null, password: uForm.password || null };
      if (editUser?.id) await api.put(`/auth/users/${editUser.id}`, body);
      else await api.post("/auth/users", body);
      toast.success("Utente salvato"); setEditUser(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!settings) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-page">
      <h1 className="font-heading text-2xl font-bold">Impostazioni</h1>

      <Tabs defaultValue="centro" className="bg-white rounded-2xl border border-slate-100 p-5">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="centro" data-testid="settings-tab-centro">Centro</TabsTrigger>
          <TabsTrigger value="trattamenti" data-testid="settings-tab-trattamenti">Trattamenti</TabsTrigger>
          <TabsTrigger value="utenti" data-testid="settings-tab-utenti">Utenti</TabsTrigger>
          <TabsTrigger value="sicurezza" data-testid="settings-tab-sicurezza">Sicurezza</TabsTrigger>
          <TabsTrigger value="archiviazione" data-testid="settings-tab-archiviazione">Archiviazione</TabsTrigger>
          <TabsTrigger value="comunicazioni" data-testid="settings-tab-comunicazioni">Comunicazioni</TabsTrigger>
          <TabsTrigger value="log" data-testid="settings-tab-log">Log attività</TabsTrigger>
        </TabsList>

        <TabsContent value="centro" className="mt-4 space-y-4 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome centro</Label><Input data-testid="settings-center-name" value={settings.center_name || ""} onChange={(e) => setSettings({ ...settings, center_name: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Telefono</Label><Input data-testid="settings-phone" value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Email</Label><Input data-testid="settings-email" value={settings.email || ""} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Indirizzo</Label><Input data-testid="settings-address" value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Orari apertura</Label><Input data-testid="settings-hours" value={settings.opening_hours || ""} onChange={(e) => setSettings({ ...settings, opening_hours: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Durata standard appuntamento (min)</Label><Input data-testid="settings-duration" type="number" value={settings.default_duration || 45} onChange={(e) => setSettings({ ...settings, default_duration: Number(e.target.value) })} className="rounded-xl" /></div>
            <div className="col-span-2"><Label>Categorie spese (separate da virgola)</Label><Input data-testid="settings-expense-cats" value={(settings.expense_categories || []).join(", ")} onChange={(e) => setSettings({ ...settings, expense_categories: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-xl" /></div>
            <div className="col-span-2"><Label>Metodi di pagamento (separati da virgola)</Label><Input data-testid="settings-pay-methods" value={(settings.payment_methods || []).join(", ")} onChange={(e) => setSettings({ ...settings, payment_methods: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-xl" /></div>
          </div>
          <Button data-testid="settings-save" onClick={saveSettings} className="rounded-full"><Save size={15} className="mr-1.5" /> Salva impostazioni</Button>
        </TabsContent>

        <TabsContent value="trattamenti" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button data-testid="treatment-new" onClick={() => { setTForm(EMPTY_TREATMENT); setEditTreatment({}); }} className="rounded-full"><Plus size={15} className="mr-1" /> Nuovo trattamento</Button>
          </div>
          <div className="space-y-2">
            {treatments.map((t) => (
              <div key={t.id} data-testid={`treatment-row-${t.id}`} className="flex items-center gap-3 border border-slate-100 rounded-xl px-4 py-3">
                <div className="flex-1">
                  <p className="font-medium text-sm">{t.name} {!t.active && <span className="text-xs text-slate-400">(disattivato)</span>}</p>
                  <p className="text-xs text-slate-400">€{t.price} · {t.duration_minutes} min
                    {t.machine_type_ids?.length > 0 && ` · macchinari: ${t.machine_type_ids.map((id) => machineTypes.find((m) => m.id === id)?.name).filter(Boolean).join(", ")}`}
                  </p>
                </div>
                <button data-testid={`treatment-edit-${t.id}`} onClick={() => { setTForm({ ...EMPTY_TREATMENT, ...t }); setEditTreatment(t); }} className="p-1.5 text-slate-400 hover:text-blue-600"><Pencil size={14} /></button>
                <button data-testid={`treatment-delete-${t.id}`} onClick={() => delTreatment(t)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="utenti" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button data-testid="user-new" onClick={() => { setUForm(EMPTY_USER); setEditUser({}); }} className="rounded-full"><Plus size={15} className="mr-1" /> Nuovo utente</Button>
          </div>
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 border border-slate-100 rounded-xl px-4 py-3">
                <div className="flex-1">
                  <p className="font-medium text-sm">{u.name} <span className="text-xs text-slate-400">{u.email}</span></p>
                  <p className="text-xs text-slate-400 capitalize">{u.role} {!u.active && "· disattivato"} {u.permissions && <span className="text-blue-500 font-medium">· permessi personalizzati</span>}</p>
                </div>
                <button data-testid={`user-edit-${u.id}`} onClick={() => { setUForm({ ...EMPTY_USER, ...u, password: "", physio_id: u.physio_id || "" }); setPermDraft(u.permissions ? JSON.parse(JSON.stringify(u.permissions)) : null); setEditUser(u); }} className="p-1.5 text-slate-400 hover:text-blue-600"><Pencil size={14} /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sicurezza" className="mt-4 max-w-2xl space-y-4" data-testid="settings-security">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3" data-testid="sec-turnstile-card">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold">Cloudflare Turnstile</h3>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${stStatus?.keys?.turnstile ? "bg-emerald-100 text-emerald-700" : secForm.turnstile_enabled ? "bg-red-100 text-red-600" : "bg-slate-200 text-slate-500"}`} data-testid="sec-turnstile-state">
                ● {stStatus?.keys?.turnstile ? "Configurato" : secForm.turnstile_enabled ? "Errore configurazione" : "Non configurato"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="sec-turnstile-enabled" checked={!!secForm.turnstile_enabled} onCheckedChange={(v) => setSecForm({ ...secForm, turnstile_enabled: v })} />
              <Label>Abilita Cloudflare Turnstile</Label>
            </div>
            <div>
              <Label>Site Key</Label>
              <Input data-testid="sec-turnstile-site-key" value={secForm.turnstile_site_key} onChange={(e) => setSecForm({ ...secForm, turnstile_site_key: e.target.value })} className="rounded-xl bg-white mt-1" placeholder="0x4AAAA..." autoComplete="off" />
            </div>
            <div>
              <Label>Secret Key</Label>
              <Input data-testid="sec-turnstile-secret-key" type="password" value={secForm.turnstile_secret_key} onChange={(e) => setSecForm({ ...secForm, turnstile_secret_key: e.target.value })} className="rounded-xl bg-white mt-1" placeholder={secForm.turnstile_secret_key ? "" : "••••••••••••••••••••"} autoComplete="new-password" />
              <p className="text-[11px] text-slate-400 mt-1">La Secret Key resta sul server e non viene mai mostrata integralmente. Lascia il valore mascherato invariato per mantenere la chiave attuale.</p>
            </div>
            <p className="text-xs text-slate-500">Le chiavi possono essere generate gratuitamente dal pannello Cloudflare Turnstile.</p>
            <div className="flex gap-2">
              <Button data-testid="sec-turnstile-save" onClick={saveSecurity} className="rounded-full"><Save size={15} className="mr-1.5" /> Salva configurazione</Button>
              <Button data-testid="sec-turnstile-test" variant="outline" onClick={testTurnstile} disabled={tsTesting} className="rounded-full">{tsTesting ? "Test in corso..." : "Testa configurazione"}</Button>
            </div>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <h3 className="font-heading font-bold">Protezione accessi</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Max tentativi login</Label><Input data-testid="sec-max-attempts" type="number" min="2" max="20" value={secForm.max_attempts} onChange={(e) => setSecForm({ ...secForm, max_attempts: Number(e.target.value) })} className="rounded-xl bg-white" /></div>
              <div><Label>Durata blocco (min)</Label><Input data-testid="sec-lockout-minutes" type="number" min="1" max="1440" value={secForm.lockout_minutes} onChange={(e) => setSecForm({ ...secForm, lockout_minutes: Number(e.target.value) })} className="rounded-xl bg-white" /></div>
              <div><Label>Timeout sessione (ore)</Label><Input data-testid="sec-session-hours" type="number" min="1" max="72" value={secForm.session_hours} onChange={(e) => setSecForm({ ...secForm, session_hours: Number(e.target.value) })} className="rounded-xl bg-white" /></div>
            </div>
            <p className="text-xs text-slate-500">Dopo N tentativi errati l'account/IP viene bloccato temporaneamente. Accessi riusciti e falliti sono registrati nel Log attività.</p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 flex items-center gap-3">
            <Switch data-testid="sec-twofa" checked={false} disabled />
            <div>
              <Label>Autenticazione a due fattori (2FA)</Label>
              <p className="text-xs text-slate-400">Predisposta a livello di dati utente — attivazione disponibile in una prossima versione.</p>
            </div>
            <span className="ml-auto text-[10px] uppercase tracking-widest bg-blue-100 text-blue-600 px-2 py-1 rounded-full">In arrivo</span>
          </div>
          <Button data-testid="sec-save" onClick={saveSecurity} className="rounded-full"><Save size={15} className="mr-1.5" /> Salva sicurezza</Button>
        </TabsContent>

        <TabsContent value="archiviazione" className="mt-4 max-w-2xl space-y-4" data-testid="settings-storage">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold">Archiviazione documenti</h3>
              <span className="text-xs bg-white border border-slate-200 px-2.5 py-1 rounded-full" data-testid="st-active-provider">
                Provider attivo: <strong>{stStatus?.storage_provider_active || "—"}</strong>
              </span>
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={stForm.provider} onValueChange={(v) => setStForm({ ...stForm, provider: v })}>
                <SelectTrigger data-testid="st-provider" className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatico (consigliato)</SelectItem>
                  <SelectItem value="local">Locale (server/VPS)</SelectItem>
                  <SelectItem value="nas">NAS (directory montata SMB/NFS)</SelectItem>
                  <SelectItem value="gdrive">Google Drive</SelectItem>
                  <SelectItem value="s3">S3 / Object Storage</SelectItem>
                  <SelectItem value="emergent">Emergent Object Storage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(stForm.provider === "local" || stForm.provider === "auto") && (
              <div><Label>Percorso directory (locale)</Label><Input data-testid="st-local-dir" value={stForm.local_dir} onChange={(e) => setStForm({ ...stForm, local_dir: e.target.value })} placeholder="/data/documents" className="rounded-xl bg-white" /></div>
            )}
            {stForm.provider === "nas" && (
              <>
                <div><Label>Percorso mount NAS</Label><Input data-testid="st-nas-dir" value={stForm.nas_dir} onChange={(e) => setStForm({ ...stForm, nas_dir: e.target.value })} placeholder="/mnt/nas/lanuseilab" className="rounded-xl bg-white" /></div>
                <p className="text-xs text-slate-500">Il mount SMB/NFS/SFTP va configurato a livello di sistema operativo (fstab/systemd). Le credenziali NAS restano nel server, mai nel gestionale.</p>
              </>
            )}
            {stForm.provider === "gdrive" && (
              <>
                <div><Label>ID cartella Google Drive</Label><Input data-testid="st-gdrive-folder" value={stForm.gdrive_folder_id} onChange={(e) => setStForm({ ...stForm, gdrive_folder_id: e.target.value })} placeholder="1AbCdEf..." className="rounded-xl bg-white" /></div>
                <p className="text-xs text-slate-500" data-testid="st-gdrive-status">Service account via env <code>GOOGLE_APPLICATION_CREDENTIALS</code> (path al file JSON): {stStatus?.keys?.gdrive ? <span className="text-emerald-600 font-medium">configurato</span> : <span className="text-red-500 font-medium">non configurato</span>}</p>
              </>
            )}
            {stForm.provider === "s3" && (
              <p className="text-xs text-slate-500" data-testid="st-s3-status">Parametri via env: <code>S3_ENDPOINT</code>, <code>S3_BUCKET</code>, <code>S3_REGION</code>, <code>S3_ACCESS_KEY</code>, <code>S3_SECRET_KEY</code> — bucket: {stStatus?.keys?.s3 ? <span className="text-emerald-600 font-medium">configurato</span> : <span className="text-red-500 font-medium">non configurato</span>}</p>
            )}
            <p className="text-xs text-slate-500">I documenti sono organizzati per ID paziente anonimo (es. <code>documents/patients/PAT-000123/</code>) e scaricabili solo tramite backend autenticato: nessun URL pubblico.</p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <h3 className="font-heading font-bold">Backup documenti</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Destinazione backup</Label>
                <Select value={stForm.backup_provider} onValueChange={(v) => setStForm({ ...stForm, backup_provider: v })}>
                  <SelectTrigger data-testid="st-backup-provider" className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nessuno">Nessuno</SelectItem>
                    <SelectItem value="local">Directory locale/VPS</SelectItem>
                    <SelectItem value="nas">NAS (directory montata)</SelectItem>
                    <SelectItem value="s3">S3 / Object Storage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Percorso backup</Label><Input data-testid="st-backup-dir" value={stForm.backup_dir} onChange={(e) => setStForm({ ...stForm, backup_dir: e.target.value })} placeholder="/mnt/nas/backup" className="rounded-xl bg-white" /></div>
            </div>
            <p className="text-xs text-slate-500">Il backup viene eseguito da <code>scripts/backup.sh</code> (MongoDB + documenti). Per S3 configurare le variabili <code>S3_*</code> sul server.</p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <h3 className="font-heading font-bold">Regole file</h3>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div><Label>Dimensione massima upload (MB)</Label><Input data-testid="st-max-mb" type="number" min="1" max="100" value={stForm.max_upload_mb} onChange={(e) => setStForm({ ...stForm, max_upload_mb: Number(e.target.value) })} className="rounded-xl bg-white" /></div>
              <div className="flex items-center gap-2 pt-5"><Switch data-testid="st-trash" checked={stForm.trash_enabled} onCheckedChange={(c) => setStForm({ ...stForm, trash_enabled: c })} /><Label>Cestino (soft delete) attivo</Label></div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="st-save" onClick={saveStorage} className="rounded-full"><Save size={15} className="mr-1.5" /> Salva archiviazione</Button>
            <Button data-testid="st-test" variant="outline" onClick={testStorage} disabled={stTesting} className="rounded-full">{stTesting ? "Test in corso..." : "Testa provider"}</Button>
          </div>
        </TabsContent>

        <TabsContent value="comunicazioni" className="mt-4 max-w-2xl space-y-4" data-testid="settings-communications">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <h3 className="font-heading font-bold">Comunicazioni interne</h3>
            <p className="text-sm text-slate-500">Scegli chi riceve le segnalazioni clinico-operative inviate dalla scheda paziente. Se non configuri nulla, le segnalazioni non vengono recapitate a nessuno.</p>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Ruoli destinatari</p>
              <div className="flex flex-wrap gap-3" data-testid="comm-roles">
                {[["admin", "Amministratore"], ["titolare", "Titolare"], ["segreteria", "Segreteria"], ["coordinatore", "Coordinatore"], ["fisioterapista", "Fisioterapista"]].map(([r, l]) => (
                  <label key={r} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-xl px-3 py-2 cursor-pointer">
                    <Checkbox data-testid={`comm-role-${r}`} checked={commForm.recipient_roles.includes(r)}
                      onCheckedChange={(c) => setCommForm({ ...commForm, recipient_roles: c ? [...commForm.recipient_roles, r] : commForm.recipient_roles.filter((x) => x !== r) })} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Utenti specifici destinatari</p>
              <div className="space-y-1.5" data-testid="comm-users">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-xl px-3 py-2 cursor-pointer">
                    <Checkbox data-testid={`comm-user-${u.id}`} checked={commForm.recipient_user_ids.includes(u.id)}
                      onCheckedChange={(c) => setCommForm({ ...commForm, recipient_user_ids: c ? [...commForm.recipient_user_ids, u.id] : commForm.recipient_user_ids.filter((x) => x !== u.id) })} />
                    <span className="font-medium">{u.name}</span>
                    <span className="text-xs text-slate-400">{u.email} · {u.role}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <Button data-testid="comm-save" onClick={saveCommunications} className="rounded-full"><Save size={15} className="mr-1.5" /> Salva destinatari</Button>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="activity-log">
            {logs.map((l) => (
              <div key={l.id} className="flex gap-3 text-xs py-1.5 border-b border-slate-50">
                <span className="text-slate-400 font-mono whitespace-nowrap">{new Date(l.timestamp).toLocaleString("it-IT")}</span>
                <span className="font-medium text-slate-600 whitespace-nowrap">{l.user_name}</span>
                <span className="text-slate-500">{l.detail}</span>
              </div>
            ))}
            {logs.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nessuna attività registrata</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog trattamento */}
      <Dialog open={Boolean(editTreatment)} onOpenChange={(o) => !o && setEditTreatment(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="treatment-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editTreatment?.id ? "Modifica trattamento" : "Nuovo trattamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="treatment-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prezzo (€)</Label><Input data-testid="treatment-price" type="number" min="0" value={tForm.price} onChange={(e) => setTForm({ ...tForm, price: Number(e.target.value) })} className="rounded-xl" /></div>
              <div><Label>Durata (min)</Label><Input data-testid="treatment-duration" type="number" value={tForm.duration_minutes} onChange={(e) => setTForm({ ...tForm, duration_minutes: Number(e.target.value) })} className="rounded-xl" /></div>
            </div>
            <div>
              <Label>Tipologie macchinari utilizzabili</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="treatment-machine-types">
                {machineTypes.map((mt) => {
                  const on = tForm.machine_type_ids.includes(mt.id);
                  return (
                    <button key={mt.id} data-testid={`treatment-mtype-${mt.id}`}
                      onClick={() => setTForm({ ...tForm, machine_type_ids: on ? tForm.machine_type_ids.filter((x) => x !== mt.id) : [...tForm.machine_type_ids, mt.id] })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-200"}`}>
                      {mt.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch data-testid="treatment-active" checked={tForm.active} onCheckedChange={(c) => setTForm({ ...tForm, active: c })} /><Label>Attivo</Label></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="treatment-cancel" onClick={() => setEditTreatment(null)} className="rounded-full">Annulla</Button>
            <Button data-testid="treatment-save" onClick={saveTreatment} className="rounded-full">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog utente */}
      <Dialog open={Boolean(editUser)} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="w-[96vw] sm:max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white" data-testid="user-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editUser?.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="profilo">
            <TabsList data-testid="user-modal-tabs" className="w-full overflow-x-auto justify-start">
              <TabsTrigger value="profilo" data-testid="user-tab-profilo">Profilo</TabsTrigger>
              <TabsTrigger value="permessi" data-testid="user-tab-permessi" disabled={!editUser?.id}>Permessi</TabsTrigger>
            </TabsList>
            <TabsContent value="profilo" className="mt-3">
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="user-name-input" value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Email *</Label><Input data-testid="user-email-input" type="email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Password {editUser?.id && "(lascia vuoto per non cambiare)"}</Label><Input data-testid="user-password-input" type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} className="rounded-xl" /></div>
            <div>
              <Label>Ruolo</Label>
              <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Amministratore</SelectItem>
                  <SelectItem value="titolare">Titolare</SelectItem>
                  <SelectItem value="segreteria">Segreteria</SelectItem>
                  <SelectItem value="coordinatore">Coordinatore / Responsabile</SelectItem>
                  <SelectItem value="fisioterapista">Fisioterapista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {uForm.role === "fisioterapista" && (
              <div>
                <Label>Fisioterapista collegato</Label>
                <Select value={uForm.physio_id} onValueChange={(v) => setUForm({ ...uForm, physio_id: v })}>
                  <SelectTrigger data-testid="user-physio-select" className="rounded-xl"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{physios.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2"><Switch data-testid="user-active" checked={uForm.active} onCheckedChange={(c) => setUForm({ ...uForm, active: c })} /><Label>Attivo</Label></div>
          </div>
            </TabsContent>
            <TabsContent value="permessi" className="mt-3" data-testid="user-permissions-tab">
              {!permMeta ? <p className="text-sm text-slate-400">Caricamento...</p> : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" data-testid="perm-reset" onClick={() => setPermDraft(null)} className="rounded-full text-xs">Ripristina permessi del ruolo</Button>
                    <Button size="sm" variant="outline" data-testid="perm-all" onClick={permAll} className="rounded-full text-xs">Seleziona tutto</Button>
                    <Button size="sm" variant="outline" data-testid="perm-none" onClick={() => setPermDraft({})} className="rounded-full text-xs">Deseleziona tutto</Button>
                    <Select onValueChange={copyPermsFrom}>
                      <SelectTrigger data-testid="perm-copy-from" className="rounded-full h-8 text-xs w-48"><SelectValue placeholder="Copia permessi da..." /></SelectTrigger>
                      <SelectContent>
                        {users.filter((u) => u.id !== editUser?.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs" data-testid="perm-state">
                    {permDraft === null
                      ? <span className="text-slate-500">Permessi <strong>ereditati dal ruolo</strong> ({editUser?.role}). Modifica una casella per personalizzarli.</span>
                      : <span className="text-blue-600 font-medium">Permessi personalizzati attivi per questo utente.</span>}
                  </p>
                  <div className="w-full overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-sm">
                    <table className="w-full min-w-[920px] text-xs bg-white" data-testid="perm-matrix">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50">
                          <th className="py-2 px-3">Sezione</th>
                          {permMeta.actions.map((a) => <th key={a.key} className="px-1.5 text-center">{a.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {permMeta.sections.map((s) => {
                          const eff = permDraft ?? permMeta.presets?.[editUser?.role] ?? {};
                          return (
                            <tr key={s.key} className="border-b border-slate-100 bg-white hover:bg-slate-50/60">
                              <td className="py-1.5 px-3 font-medium whitespace-nowrap">{s.label}</td>
                              {permMeta.actions.map((a) => {
                                const checked = (eff[s.key] || []).includes(a.key);
                                const dim = (a.key === "download" || a.key === "upload") && s.key !== "documenti";
                                return (
                                  <td key={a.key} className="text-center">
                                    <input type="checkbox" data-testid={`perm-${s.key}-${a.key}`} checked={checked} disabled={dim}
                                      onChange={() => togglePerm(s.key, a.key)}
                                      className={`w-3.5 h-3.5 accent-blue-600 ${dim ? "opacity-15" : "cursor-pointer"}`} />
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-400">Nota: "Amministra" include tutte le altre azioni della sezione. Per i fisioterapisti, "Pazienti → Amministra" rimuove il limite dei pazienti assegnati.</p>
                  <Button data-testid="perm-save" onClick={savePerms} className="rounded-full"><Save size={15} className="mr-1.5" /> Salva permessi</Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="user-cancel" onClick={() => setEditUser(null)} className="rounded-full">Annulla</Button>
            <Button data-testid="user-save" onClick={saveUser} className="rounded-full">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
